import { Link } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Gauge, MapPinOff, Play, Pause, Square } from "lucide-react";
import { AppBanner } from "@/components/AppBanner";
import { RatingPrompts } from "@/components/RatingPrompts";
import { PortalShell } from "@/components/AppHeader";
import { AdSlot } from "@/components/AdSlot";
import { MapView } from "@/components/MapView";
import { VehicleCard } from "@/components/VehicleCard";
import { OfferCard } from "@/pages/driver/OfferCard";
import { DriverTripPanel } from "@/pages/driver/DriverTripPanel";
import { OpenRentals } from "@/pages/driver/OpenRentals";
import { api, errMsg, money } from "@/lib/api";

const GEO_ERROR = "Location access required to go online";
const SEND_EVERY_MS = 7000;
const BADGE = {
  online: ["Online", "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"],
  paused: ["Paused", "border-amber-500/50 bg-amber-500/10 text-amber-300"],
  offline: ["Offline", "border-zinc-700 bg-zinc-900/60 text-zinc-400"],
};

const getPosition = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) return reject(new Error("unsupported"));
  navigator.geolocation.getCurrentPosition((p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, speed: p.coords.speed ?? null }), reject, { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 });
});

const AvailabilityControls = ({ mode, approved, busy, onOnline, onPause, onStop, blockedReason }) => {
  const [label, cls] = BADGE[mode];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span data-testid="driver-online-status" className={`rounded-full border px-4 py-2 font-mono text-xs tracking-widest ${cls}`}>{label}</span>
      {mode === "offline" && (
        <span data-testid="driver-online-toggle" className="inline-flex">
          <button data-testid="driver-go-online-button" disabled={busy || !approved || !!blockedReason} title={blockedReason || undefined} onClick={onOnline} className="btn-gold !px-6 !py-3 text-base">
            <Play className="h-4 w-4" /> Go Online
          </button>
        </span>
      )}
      {mode === "paused" && (
        <span data-testid="driver-back-online-button" className="inline-flex">
          <span data-testid="driver-online-toggle" className="inline-flex">
            <button data-testid="driver-resume-button" disabled={busy || !!blockedReason} title={blockedReason || undefined} onClick={onOnline} className="btn-gold !px-8 !py-4 text-lg shadow-[0_0_24px_rgba(212,175,55,0.35)]">
              <Play className="h-5 w-5" /> Back Online
            </button>
          </span>
        </span>
      )}
      {mode === "online" && (
        <button data-testid="driver-pause-button" disabled={busy} onClick={onPause} className="btn-outline-gold !px-6 !py-3 text-base"><Pause className="h-4 w-4" /> Pause</button>
      )}
      {mode !== "offline" && (
        <span data-testid="driver-offline-button" className="inline-flex">
          <span data-testid={mode === "online" ? "driver-online-toggle" : "driver-offline-toggle"} className="inline-flex">
            <button data-testid="driver-stop-button" disabled={busy} onClick={onStop} className="btn-danger !px-6 !py-3 text-base"><Square className="h-4 w-4" /> Offline</button>
          </span>
        </span>
      )}
    </div>
  );
};

const IDLE_TEXT = {
  online: ["Waiting for ride requests", "Your location is shared every few seconds. Requests from the nearest riders appear here with 60 seconds to accept."],
  paused: ["Paused — no new requests", "No new rides — your current trip continues and your GPS keeps sharing. Tap Back Online to receive requests again."],
  offline: ["You're offline", "Offline — no ride requests. Your GPS location keeps sharing while you're signed in. Tap Go Online to receive requests."],
};
const GPS_BANNER = "GPS must stay on — turn location back on";
const hm = (s) => `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;

const DutyCard = ({ state }) => {
  const d = state.duty;
  return (
    <div className="grid gap-3 sm:grid-cols-2" data-testid="driver-duty-card">
      <div className="lux-card p-4"><p className="lux-eyebrow !text-[0.6rem]">Driving hours remaining</p>
        <p data-testid="driver-hours-remaining" className={`mt-1 font-serif text-2xl ${d.limit_reached ? "text-red-300" : "text-amber-100"}`}>{d.limit_reached ? "0h 0m" : hm(d.remaining_seconds)}</p>
        <p className="text-xs text-zinc-500">{hm(d.driving_seconds)} driven of 12h</p></div>
      <div className="lux-card p-4"><p className="lux-eyebrow !text-[0.6rem]">Training</p>
        {state.user.training_passed ? <p data-testid="driver-training-ok" className="mt-1 text-sm text-emerald-300">Passed — ready to drive</p>
          : <Link to="/driver/training" data-testid="driver-training-required" className="mt-1 block text-sm font-medium text-amber-400 underline">Complete training before driving</Link>}</div>
      {d.message && <p data-testid="driver-limit-message" className="rounded-xl border border-red-500/40 bg-red-950/60 px-4 py-3 text-sm text-red-200 sm:col-span-2">{d.message}{d.rest_remaining_seconds > 0 && ` · ${hm(d.rest_remaining_seconds)} of rest remaining`}</p>}
    </div>
  );
};
const GPS_STALE_MS = 60000;

const GpsBanner = ({ onRetry }) => (
  <div data-testid="driver-gps-banner" className="sticky top-20 z-[1100] flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-500/50 bg-red-950/90 px-4 py-3 text-sm text-red-200 backdrop-blur">
    <span className="flex items-center gap-2"><MapPinOff className="h-4 w-4" />{GPS_BANNER}</span>
    <button data-testid="driver-gps-retry" onClick={onRetry} className="btn-outline-gold !px-4 !py-1.5 text-xs">Turn on location</button>
  </div>
);

const EarningsHeader = ({ totals, income }) => (
  <div className="space-y-3" data-testid="driver-earnings-header">
    <Link to="/driver/earnings" className="lux-card block p-5 hover:border-amber-400/40 sm:p-6" data-testid="driver-total-earnings">
      <p className="lux-eyebrow">Total earnings</p>
      <p className="mt-1 text-xs text-zinc-500">Today</p>
      <p data-testid="driver-earnings-today" className="gold-text font-serif text-4xl font-semibold sm:text-5xl">{money(totals.today)}</p>
      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-zinc-400">
        <span>This week <b data-testid="driver-earnings-week" className="text-amber-100">{money(totals.this_week)}</b></span>
        <span>All time <b data-testid="driver-earnings-all-time" className="text-amber-100">{money(totals.all_time)}</b></span>
      </div>
    </Link>
    {income && (
      <div className="lux-card border-amber-400/30 p-5" data-testid="driver-trip-income">
        <div className="flex items-baseline justify-between gap-3">
          <p className="lux-eyebrow">This trip — your ride income</p>
          <p data-testid="driver-trip-income-total" className="gold-text font-serif text-3xl font-semibold">{money(income.total)}</p>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-zinc-400 sm:grid-cols-4">
          <span>Fare ({income.share_percent}%) <b data-testid="driver-trip-income-fare" className="text-amber-100">{money(income.fare_share)}</b></span>
          <span>Wait <b data-testid="driver-trip-income-wait" className="text-amber-100">{money(income.wait)}</b>{income.wait_status === "pending" && <span data-testid="driver-trip-income-wait-pending" className="ml-1 text-zinc-500">(pending)</span>}</span>
          <span>Tolls <b data-testid="driver-trip-income-tolls" className="text-amber-100">{money(income.tolls)}</b></span>
          <span>Tip <b data-testid="driver-trip-income-tip" className="text-amber-100">{money(income.tip)}</b></span>
        </div>
      </div>
    )}
  </div>
);

export default function DriverHome() {
  const [state, setState] = useState(null);
  const [position, setPosition] = useState(null);
  const [geoError, setGeoError] = useState("");
  const [busy, setBusy] = useState(false);
  const posRef = useRef(null);

  const load = useCallback(async () => {
    try { setState((await api.get("/driver/state")).data); } catch { /* retry next tick */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);

  const mode = state?.user?.availability || "offline";
  const signedIn = !!state;
  const lastFix = useRef(0);
  const [gpsOff, setGpsOff] = useState(false);
  const gpsOffRef = useRef(false);

  const markGps = useCallback((ok) => {
    if (ok) lastFix.current = Date.now();
    if (gpsOffRef.current === !ok) return;
    gpsOffRef.current = !ok;
    setGpsOff(!ok);
    if (!ok) api.post("/driver/gps-off").catch(() => {});
  }, []);

  const fix = useCallback(async () => {
    try { posRef.current = await getPosition(); setPosition(posRef.current); markGps(true); }
    catch (e) { if (e?.code === 1 || e?.message === "unsupported") markGps(false); }
    if (posRef.current && Date.now() - lastFix.current < GPS_STALE_MS) api.post("/driver/location", posRef.current).catch(() => {});
    else if (lastFix.current && Date.now() - lastFix.current >= GPS_STALE_MS) markGps(false);
  }, [markGps]);

  useEffect(() => {
    if (!signedIn) return;
    const watch = navigator.geolocation?.watchPosition((p) => {
      posRef.current = { lat: p.coords.latitude, lng: p.coords.longitude, speed: p.coords.speed ?? null };
      setPosition(posRef.current);
      markGps(true);
    }, (e) => { if (e.code === 1) markGps(false); }, { enableHighAccuracy: true, maximumAge: 3000 });
    const onVisible = () => { if (document.visibilityState === "visible") fix(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    fix();
    const t = setInterval(fix, SEND_EVERY_MS);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      if (watch != null) navigator.geolocation.clearWatch(watch);
    };
  }, [signedIn, fix, markGps]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); await load(); } catch (e) { toast.error(errMsg(e)); await load(); } finally { setBusy(false); }
  };
  const goOnline = () => run(async () => {
    setGeoError("");
    let p;
    try { p = await getPosition(); } catch { setGeoError(GEO_ERROR); return; }
    posRef.current = p;
    setPosition(p);
    await api.post("/driver/online", p);
  });

  if (!state) return <PortalShell><div /></PortalShell>;
  const { user, offer, active_trip: trip, fees } = state;
  const approved = user.driver_status === "approved";
  const [idleTitle, idleBody] = IDLE_TEXT[mode];

  return (
    <PortalShell>
      <div data-testid="driver-dashboard" className="space-y-6">
        {state.earnings_totals && <EarningsHeader totals={state.earnings_totals} income={trip?.driver_income} />}
        <Link to="/driver/hub" data-testid="driver-hub-link" className="lux-card flex items-center justify-between gap-3 border-amber-400/30 px-5 py-4 transition-colors duration-300 hover:border-amber-400/60">
          <span><span className="block font-serif text-xl text-amber-50">Driver Hub</span><span className="block text-xs text-zinc-400">Discounts, bonuses, trips, rules, training, news & help</span></span>
          <span className="text-sm font-semibold text-amber-300">Open →</span>
        </Link>
        {approved && <AppBanner />}
        {approved && <RatingPrompts />}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="lux-eyebrow">Driver console</p>
            <h1 className="mt-2 text-3xl font-semibold text-amber-50 sm:text-4xl">Good evening, {user.name.split(" ")[0]}</h1>
            {user.vehicle && <p className="mt-1 text-sm text-zinc-400">{user.vehicle}</p>}
            {user.driver_code && <p className="mt-1 text-sm text-zinc-400">Driver ID <span data-testid="driver-code" className="font-mono text-amber-300">{user.driver_code}</span></p>}
          </div>
          <AvailabilityControls mode={mode} approved={approved} busy={busy} blockedReason={!state?.user?.training_passed ? "Complete training before driving" : state?.duty?.limit_reached ? state.duty.message : null} onOnline={goOnline}
            onPause={() => run(() => api.post("/driver/pause"))} onStop={() => run(() => api.post("/driver/offline"))} />
        </div>
        {gpsOff && <GpsBanner onRetry={fix} />}
        {state.speeding?.current_warning && (
          <div data-testid="speeding-warning" role="alert" className="flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            <Gauge className="mt-0.5 h-5 w-5 shrink-0 text-red-300" />
            <div><p className="font-semibold" data-testid="speeding-warning-text">{state.speeding.current_warning.message}</p>
              <p className="mt-0.5 text-xs text-red-300/80">{state.speeding.this_month} speeding event{state.speeding.this_month === 1 ? "" : "s"} this month · 4 in a month adds a speeding record</p></div>
          </div>
        )}
        {state && <DutyCard state={state} />}
        {state.cancel_rules?.termination_pending && <p data-testid="driver-contract-review-notice" role="alert" className="rounded-xl border border-red-500/40 bg-red-950/60 px-4 py-3 text-sm font-semibold text-red-200">{state.cancel_rules.notice} — the company is reviewing your cancellations after arriving.</p>}
        {state.cancel_rules && (
          <details className="lux-card p-4 text-sm" data-testid="driver-rules-cancellations">
            <summary className="cursor-pointer font-medium text-amber-100">Driver rules: cancellations <span data-testid="driver-arrival-cancels-count" className="ml-2 text-xs text-zinc-500">{state.cancel_rules.arrival_cancels_this_month} after arriving this month</span></summary>
            <ul className="mt-3 list-disc space-y-1.5 pl-5 text-zinc-400">{state.cancel_rules.rules.map((r) => <li key={r}>{r}</li>)}</ul>
          </details>
        )}
        {!approved && <p data-testid="driver-pending-message" className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">Your driver account is pending admin approval. You can go online once approved.</p>}
        {geoError && <p data-testid="driver-location-error" className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"><MapPinOff className="h-4 w-4" />{geoError}</p>}
        {trip && mode !== "online" && <p data-testid="driver-no-new-requests-note" className="text-xs text-zinc-500">{mode === "paused" ? "Paused" : "Stopped"} — no new requests. Your current trip continues normally.</p>}
        {trip ? (
          <DriverTripPanel trip={trip} fees={fees} position={position || trip.driver_location} busy={busy} onAction={(a, body) => run(() => api.post(`/driver/trips/${trip.id}/${a}`, body))} />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
            <div className="lux-card p-6">
              <p className="lux-eyebrow">Status</p>
              <h2 className="mt-3 text-2xl text-amber-50" data-testid="driver-idle-message">{idleTitle}</h2>
              <p className="mt-3 text-sm text-zinc-400">{idleBody}</p>
              {position && <p data-testid="driver-current-location" className="mt-6 font-mono text-xs text-zinc-500">{position.lat.toFixed(5)}, {position.lng.toFixed(5)} · GPS sharing</p>}
              <div className="mt-6"><VehicleCard detail={user.vehicle} testId="driver-vehicle-card" /></div>
            </div>
            <MapView className="h-[320px] sm:h-[420px]" driver={position} testId="driver-map" />
          </div>
        )}
        {offer && !trip && mode === "online" && !gpsOff && <OfferCard offer={offer} busy={busy} onAccept={() => run(() => api.post(`/driver/trips/${offer.id}/accept`))} onDecline={() => run(() => api.post(`/driver/trips/${offer.id}/decline`))} />}
        {!trip && approved && <OpenRentals onAccepted={load} />}
        {!trip && !offer && <AdSlot placement="driver_console" className="mt-8" />}
      </div>
    </PortalShell>
  );
}
